import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Api } from 'lib/api.mjs';
import { authService } from 'services';
import AdminPreviewBar from 'components/AdminPreviewBar.jsx';

/**
 * Who is signed in.
 *
 * The transport itself lives in lib/api.mjs; what lives HERE is the part that
 * needs React — the token, and the rule that a 401 on any request signs the
 * user out. Api.configure() hands those two things down, so a service module
 * can be a plain function of its payload without threading auth through every
 * call.
 *
 * api() is kept as the escape hatch for call sites that have not moved to a
 * service yet. It takes a full '/api/...' path and normalises it, so the two
 * styles can coexist during the changeover.
 *
 * Storage keys are unchanged from public/index.html (af_token / af_role /
 * af_name), so a user with an existing session stays signed in.
 */

const AuthContext = createContext(null);

const read = k => { try { return globalThis.sessionStorage?.getItem(k) || globalThis.localStorage?.getItem(k); } catch { return null; } };

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => read('af_token'));
  const [role, setRole]   = useState(() => read('af_role'));
  const [name, setName]   = useState(() => read('af_name'));
  const [superToken, setSuperToken] = useState(() => {
    try { return sessionStorage.getItem('super_admin_token'); } catch { return null; }
  });

  const logout = useCallback(() => {
    // The "what's new" read marker is server-side and per username, so clearing
    // localStorage cannot lose it. See UpdatesContext.
    try { for (const k of ['af_token','af_role','af_name','af_super_view','super_admin_token']) sessionStorage.removeItem(k); localStorage.clear(); } catch { /* private mode */ }
    setToken(null);
    setRole(null);
    setName(null);
    setSuperToken(null);
  }, []);

  /*
   * Hand the token and the sign-out rule to the transport.
   *
   * Read through refs so Api.configure runs once rather than on every token
   * change — a service module captured at import time would otherwise hold a
   * stale getter.
   */
  const tokenRef = useRef(token);
  const logoutRef = useRef(logout);
  tokenRef.current = token;
  logoutRef.current = logout;

  /*
   * Configured during the first RENDER, not in an effect.
   *
   * React runs child effects before parent ones, so DashDataProvider's mount
   * effect fires its first request before an effect here could run. That
   * request would go out with no Authorization header, come back 401, and sign
   * the user straight back out. Doing it here means the transport is ready
   * before any child mounts.
   */
  const configured = useRef(false);
  if (!configured.current) {
    configured.current = true;
    Api.configure({
      getToken: () => tokenRef.current,
      onUnauthorized: () => logoutRef.current(),
    });
  }

  /**
   * The pre-service call style, kept while call sites move across.
   *
   * Takes a full '/api/...' path where a service takes '/...', so both can be
   * in use at once without two base paths to keep straight. Returns null on 401
   * (after signing out) and on a body that will not parse — callers guard with
   * `res?.field`, so changing that to a throw would break them silently.
   */
  const api = useCallback((method, url, body, { signal } = {}) => {
    const path = url.startsWith('/api') ? url.slice('/api'.length) : url;
    return Api.request(path, method, { body: body ?? undefined, signal });
  }, []);

  const login = useCallback(async (username, password, organization) => {
    // loginUser opts out of the Authorization header: sending a stale token
    // would 401 and sign out the person trying to sign in.
    const d = await authService.loginUser({ username, password, organization });

    // The transport returns null for a network failure as well as for bad
    // credentials, and those need different wording — a wrong password is the
    // user's problem to fix, an unreachable server is not.
    if (d === null && !navigator.onLine) {
      return { ok: false, error: 'Could not reach the server' };
    }
    if (!d?.token) return { ok: false, error: typeof d?.error === 'string' ? d.error : 'Invalid credentials' };

    if (d.role === 'super_admin') {
      try {
        sessionStorage.setItem('super_admin_token', d.token);
        for (const k of ['af_token','af_role','af_name','af_super_view']) sessionStorage.removeItem(k);
      } catch { return { ok: false, error: 'Allow session storage to use organization management' }; }
      setSuperToken(d.token);
      return { ok: true, role: d.role };
    }

    try {
      for (const k of ['af_token','af_role','af_name','af_super_view']) sessionStorage.removeItem(k);
      localStorage.setItem('af_token', d.token);
      localStorage.setItem('af_role', d.role);
      localStorage.setItem('af_name', d.name || username);
    } catch { /* private mode — the session still works for this tab */ }

    setToken(d.token);
    setRole(d.role);
    setName(d.name || username);
    return { ok: true };
  }, []);

  const value = useMemo(() => ({
    token, role, name, api, login, logout, superToken,
    isSuperAdmin: Boolean(superToken) && !read('af_super_view'),
    isAdminPreview: Boolean(read('af_super_view')),
    isSignedIn: Boolean(token),
    isAdmin: role === 'admin',
  }), [token, role, name, api, login, logout, superToken]);

  return <AuthContext.Provider value={value}>
    {read('af_super_view') && <AdminPreviewBar organization={read('af_super_view')} onReturn={() => {
      for (const k of ['af_token','af_role','af_name','af_super_view']) sessionStorage.removeItem(k);
      window.location.assign('/');
    }} />}
    {children}
  </AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
